import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import { ResponsiveLine } from '@nivo/line';
import { toBrCurrency } from '../../utils/utils';

interface GraphPoint {
	period: string;
	value: number;
}

interface GraphPointXY {
	x: string;
	y: number;
}

interface LineChart {
	id: string;
	color: string;
	data: GraphPoint[];
}

interface LineChartXY {
	id: string;
	color: string;
	data: GraphPointXY[];
}

interface GraphPointXYData {
	x: string;
	y: number;
	xFormatted: string;
	yFormatted: string;
	percentOfGoal: number;
}

interface Props {
	dadosGrafico: LineChart[];
}

const colors = ['hsl(211, 100%, 50%)', 'hsl(134, 61%, 41%)'];

const WeeklySalesGoal = ({ dadosGrafico }: Props) => {
	const convertData = (gradosGraficoRaw: LineChart[]): LineChartXY[] =>
		gradosGraficoRaw?.map((dadoLinha) => {
			const data = dadoLinha.data.map((graphPoint) => {
				let percentOfGoal = 0;

				if (dadoLinha.id === 'Realizado' && graphPoint.value > 0) {
					const realizadoPeriodo = graphPoint.period;
					const valorRealizado = graphPoint.value;

					const objetivoFiltered = gradosGraficoRaw.find(
						(dadoLinha) => dadoLinha?.id === 'Objetivo',
					);

					const objetivoSamePeriodo = (objetivoFiltered?.data || []).find(
						(objetivoData) => objetivoData?.period === realizadoPeriodo,
					);
					const valorObjetivo = objetivoSamePeriodo?.value;

					const percRaw = valorRealizado / (valorObjetivo ?? 1);

					percentOfGoal = parseFloat((percRaw * 100).toFixed(2));
				}

				return {
					percentOfGoal,
					x: graphPoint.period,
					y: graphPoint.value,
				};
			});

			const replaceTextId = dadoLinha.id === 'Objetivo' ? 'Meta' : 'Venda';

			const reducedData = data.length > 30 ? data.slice(0, 24) : data;

			return {
				...dadoLinha,
				id: replaceTextId,
				data: reducedData,
			};
		});

	// const convertedData = useMemo(() => convertData(a), [a]);

	const convertedData = useMemo(() => {
		if (dadosGrafico?.length) {
			return convertData(dadosGrafico);
		}
		return [];
	}, [dadosGrafico]);

	const renderPointLabel = (d: GraphPointXYData) => {
		if (d.percentOfGoal === 0) return '';
		return `${d.percentOfGoal}%`;
	};

	const renderTooltip = (d: any) => {
		if (d?.point?.serieId) {
			const money = parseFloat(d?.point?.data?.yFormatted) ?? 0;
			const formattedMoney = toBrCurrency(money);
			return (
				<div
					style={{
						backgroundColor: 'white',
						border: '1px solid black',
						padding: '6px',
					}}
				>
					{d?.point?.serieId}: R$ {formattedMoney}
				</div>
			);
		}
		return '';
	};

	const formatAxisLeft = (value: number) => {
		if (!value) return 0;
		if (value > 999999) return value / 1000000;

		return value / 100;
	};
	return (
		<div className="div-indicador div-indicador-wide">
			{/* @ts-ignore */}
			<Card>
				{/* @ts-ignore */}
				<Card.Header style={{ fontWeight: 'bold' }}>
					Meta x Venda Semanal (em milhares)
				</Card.Header>
				{/* @ts-ignore */}
				<Card.Body>
					<div className="pie">
						{/* @ts-ignore */}
						<ResponsiveLine
							data={convertedData}
							margin={{ top: 30, right: 80, bottom: 40, left: 70 }}
							xScale={{ type: 'point' }}
							yScale={{
								type: 'linear',
								min: '0',
								max: 'auto',
								stacked: false,
								reverse: false,
							}}
							// legend={{
							// 	text: {
							// 		fontSize: 2,
							// 	},
							// }}
							yFormat=" >-.2f"
							axisTop={null}
							axisRight={null}
							axisBottom={{
								tickSize: 5,
								tickPadding: 5,
								tickRotation: 45,
								legend: 'Semana',
								legendOffset: 36,
								legendPosition: 'middle',
								truncateTickAt: 0,
							}}
							axisLeft={{
								tickSize: 5,
								tickPadding: 25,
								tickRotation: 0,
								legend: 'Meta (R$)',
								legendOffset: -60,
								legendPosition: 'middle',
								truncateTickAt: 0,
								format: formatAxisLeft,
							}}
							colors={colors}
							lineWidth={3}
							enableArea
							enablePoints
							enablePointLabel
							pointLabel={renderPointLabel}
							pointSize={6}
							pointColor={(d: any) => d.color}
							pointBorderWidth={2}
							pointBorderColor={(d: any) => d.serieColor}
							enableTouchCrosshair={true}
							useMesh={true}
							tooltip={renderTooltip}
							theme={{
								dots: {
									text: {
										fontSize: 9,
									},
								},
							}}
							legends={[
								{
									anchor: 'bottom-right',
									direction: 'column',
									justify: false,
									translateX: 100,
									translateY: 0,
									itemsSpacing: 0,
									itemDirection: 'left-to-right',
									itemWidth: 80,
									itemHeight: 20,
									itemOpacity: 0.75,
									symbolSize: 12,
									symbolShape: 'circle',
									symbolBorderColor: 'rgba(0, 0, 0, .5)',
									effects: [
										{
											on: 'hover',
											style: {
												itemBackground: 'rgba(0, 0, 0, .03)',
												itemOpacity: 1,
											},
										},
									],
								},
							]}
						/>
					</div>
				</Card.Body>
			</Card>
		</div>
	);
};

export default WeeklySalesGoal;

// const a = [
// 	{
// 		id: 'Objetivo',
// 		color: 'hsl(211, 100%, 50%)',
// 		data: [
// 			{
// 				periodo: '2410',
// 				valor: 9600,
// 			},
// 			{
// 				periodo: '2411',
// 				valor: 48000,
// 			},
// 			{
// 				periodo: '2412',
// 				valor: 96000,
// 			},
// 			{
// 				periodo: '2413',
// 				valor: 57600,
// 			},
// 			{
// 				periodo: '2414',
// 				valor: 105600,
// 			},
// 			{
// 				periodo: '2415',
// 				valor: 86400,
// 			},
// 			{
// 				periodo: '2416',
// 				valor: 76800,
// 			},
// 			{
// 				periodo: '2417',
// 				valor: 76800,
// 			},
// 			{
// 				periodo: '2418',
// 				valor: 67200,
// 			},
// 			{
// 				periodo: '2419',
// 				valor: 57600,
// 			},
// 			{
// 				periodo: '2420',
// 				valor: 57600,
// 			},
// 			{
// 				periodo: '2421',
// 				valor: 48000,
// 			},
// 			{
// 				periodo: '2422',
// 				valor: 48000,
// 			},
// 			{
// 				periodo: '2423',
// 				valor: 38400,
// 			},
// 			{
// 				periodo: '2424',
// 				valor: 38400,
// 			},
// 			{
// 				periodo: '2425',
// 				valor: 28800,
// 			},
// 			{
// 				periodo: '2426',
// 				valor: 19200,
// 			},
// 		],
// 	},
// 	{
// 		id: 'Realizado',
// 		color: 'hsl(134, 61%, 41%)',
// 		data: [
// 			{
// 				periodo: '2410',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2411',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2412',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2413',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2414',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2415',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2416',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2417',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2418',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2419',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2420',
// 				valor: 129666.28,
// 			},
// 			{
// 				periodo: '2421',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2422',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2423',
// 				valor: 139793.92,
// 			},
// 			{
// 				periodo: '2424',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2425',
// 				valor: 0,
// 			},
// 			{
// 				periodo: '2426',
// 				valor: 150953.13,
// 			},
// 		],
// 	},
// ];
